import { Column, Typography } from '@tempmee/react';
import { formatYears } from '~/commonComponents/CommonFunctions';
import { useCandidate } from '~/hooks';
import { useSelectedOffice } from '~/hooks/useSelectedOffice';
import { IJob, IUser } from '~/models';
import { TNullable } from '~/types/Nullable';
import { EducationUtils } from '~/utils/EducationUtils';
import { useCompanyMatchLabel } from '../../hooks';
import { ProfessionalAttestationInfo } from '../ProfessionalAttestationInfo';
import { InfoSection } from './InfoSection';
import { DnDaProcedures, RdhFdProcedures } from '../Procedure';

interface IExperienceSkillsPreviewProps {
  job: TNullable<IJob>;
  professional: TNullable<IUser>;
  hasWorkedWithDSO?: boolean;
  hasWorkedWithOffice?: boolean;
}

export const ExperienceSkillsPreview = ({
  job,
  professional,
  hasWorkedWithDSO = false,
  hasWorkedWithOffice = false,
}: IExperienceSkillsPreviewProps) => {
  const offerOwner = useSelectedOffice(job?.offer_owner.id);

  const dataCompanyMatchLabel = useCompanyMatchLabel({
    hasWorkedWithDSO,
    offerOwnerId: job?.offer_owner?.id || '',
    professional,
    hasWorkedWithOffice,
  });

  const { specialtyMatch, userSpecialties } = useCandidate(
    offerOwner,
    professional ?? null,
    job ?? null,
  );

  const yoe =
    professional?.professional_info?.exactYearsOfExperience !== undefined
      ? formatYears(professional?.professional_info.exactYearsOfExperience)
      : professional?.years_of_experience;

  return (
    <Column gap={5} style={{ justifyContent: 'space-around' }}>
      {dataCompanyMatchLabel && (
        <InfoSection title={dataCompanyMatchLabel.label} value={dataCompanyMatchLabel.text} />
      )}
      <ProfessionalAttestationInfo consent={professional?.consent} />
      <InfoSection title="Years of Experience" value={yoe} />
      <InfoSection
        title={userSpecialties?.length > 1 ? 'Specialties' : 'Specialty'}
        value={EducationUtils.mapToRemoveSpecialtyPrefix(userSpecialties).join(', ')}
        showWarningIcon={!specialtyMatch}
      />
      <Column className="flex-1">
        <Typography as="span" bold size="heading_4">
          Experienced Procedures:
        </Typography>
        <RdhFdProcedures job={job} professional={professional} />
        <DnDaProcedures job={job} professional={professional} />
      </Column>
    </Column>
  );
};
